import { constructCsvFileName } from 'Store/AdvancedSearch/utilities/advancedSearchStoreUtils'
import { trackGa4Event, ANALYTICS_FEATURES } from 'shared/utilities/ga4'
import { normalizePagePath, shouldSkipPageView } from 'shared/utilities/pageViewUtils'
import {
  getGeographyDisplayName,
  parseDistrictDashboardPath,
  parsePropertyLookupPath,
} from 'shared/utilities/routeUtils'

const PAGE_VIEW_DEDUPE_MS = 500
let lastPageViewTracked = { path: null, at: 0 }

export const fireCsvDownloadEvent = (fileName, feature = ANALYTICS_FEATURES.TABLE) => (_, getState) => {
  trackGa4Event('csv_download', {
    feature,
    file_name: fileName,
  }, getState)
}

export const fireUserLoginEvent = () => (_, getState) => {
  trackGa4Event('login', {
    feature: ANALYTICS_FEATURES.AUTH,
  }, getState)
}

export const fireUserLogoutEvent = () => (_, getState) => {
  trackGa4Event('logout', {
    feature: ANALYTICS_FEATURES.AUTH,
  }, getState)
}

export const fireAdvancedSearchSubmitEvent = advancedSearch => (_, getState) => {
  const geography = advancedSearch.geographies?.[0]
  trackGa4Event('custom_search_submit', {
    feature: ANALYTICS_FEATURES.CUSTOM_SEARCH,
    search_summary: constructCsvFileName(advancedSearch, false),
    geography_type: geography?.constant,
    geography_id: geography?.id,
    filter_count: advancedSearch.conditions?.['0']?.filters?.filter(f => !f.conditionGroup && f.id !== 'NEW_FILTER')?.length,
  }, getState)
}

export const fireCustomSearchSaveEvent = ({ queryName, action, notificationFrequency, url }) => (_, getState) => {
  trackGa4Event('custom_search_save', {
    feature: ANALYTICS_FEATURES.CUSTOM_SEARCH,
    save_action: action,
    query_name: queryName,
    notification_frequency: notificationFrequency,
    search_url: url,
  }, getState)
}

export const fireCustomSearchSelectFilterEvent = label => (_, getState) => {
  trackGa4Event('custom_search_select_filter', {
    feature: ANALYTICS_FEATURES.CUSTOM_SEARCH,
    filter_name: label,
  }, getState)
}

export const fireCustomSearchPropertyTypeEvent = housingType => (_, getState) => {
  trackGa4Event('custom_search_property_type', {
    feature: ANALYTICS_FEATURES.CUSTOM_SEARCH,
    housing_type: housingType,
  }, getState)
}

export const fireCustomSearchSwitchConditionEvent = type => (_, getState) => {
  trackGa4Event('custom_search_switch_condition', {
    feature: ANALYTICS_FEATURES.CUSTOM_SEARCH,
    condition_type: type,
  }, getState)
}

export const fireCustomSearchAddConditionGroupEvent = () => (_, getState) => {
  trackGa4Event('custom_search_add_group', {
    feature: ANALYTICS_FEATURES.CUSTOM_SEARCH,
  }, getState)
}

export const fireDistrictDashboardViewEvent = (geographyType, geographyId) => (_, getState) => {
  trackGa4Event('district_dashboard_view', {
    feature: ANALYTICS_FEATURES.DISTRICT_DASHBOARD,
    geography_type: geographyType,
    geography_id: String(geographyId),
    geography_label: getGeographyDisplayName(geographyType),
  }, getState)
}

export const fireFilterSelectEvent = filter => (_, getState) => {
  trackGa4Event('district_dashboard_select_filter', {
    feature: ANALYTICS_FEATURES.DISTRICT_DASHBOARD,
    filter_name: filter.label || (filter.resourceModel || {}).label,
    filter_field: filter.fieldName || filter.field,
  }, getState)
}

export const fireMapDateRangeSelectEvent = value => (_, getState) => {
  trackGa4Event('district_dashboard_select_date', {
    feature: ANALYTICS_FEATURES.DISTRICT_DASHBOARD,
    date_range: value,
  }, getState)
}

export const fireDashboardToggleConditionEvent = value => (_, getState) => {
  trackGa4Event('district_dashboard_toggle_condition', {
    feature: ANALYTICS_FEATURES.DISTRICT_DASHBOARD,
    condition_type: value,
  }, getState)
}

export const firePropertyLookupViewEvent = (bbl, bin) => (_, getState) => {
  trackGa4Event('property_lookup_view', {
    feature: ANALYTICS_FEATURES.PROPERTY_LOOKUP,
    bbl,
    bin,
  }, getState)
}

export const firePropertyLookupSearchEvent = searchQuery => (_, getState) => {
  trackGa4Event('property_lookup_search', {
    feature: ANALYTICS_FEATURES.PROPERTY_LOOKUP,
    search_query: String(searchQuery || '').slice(0, 120),
  }, getState)
}

export const fireSwitchLookupTableEvent = ({
  tableName,
  lookupTab,
  resourceConstant,
  bbl,
  bin,
} = {}) => (_, getState) => {
  trackGa4Event('property_lookup_select_table', {
    feature: ANALYTICS_FEATURES.PROPERTY_LOOKUP,
    table_name: tableName,
    lookup_tab: lookupTab,
    resource_constant: resourceConstant,
    bbl,
    bin,
  }, getState)
}

export const fireTableColumnFilterEvent = (columnId, filterValue, feature) => (_, getState) => {
  trackGa4Event('table_column_filter', {
    feature: feature || ANALYTICS_FEATURES.TABLE,
    column_id: columnId,
    filter_value: String(filterValue).slice(0, 120),
  }, getState)
}

export const fireTableSortEvent = (columnId, direction, feature) => (_, getState) => {
  trackGa4Event('table_sort', {
    feature: feature || ANALYTICS_FEATURES.TABLE,
    column_id: columnId,
    sort_direction: direction,
  }, getState)
}

export const fireTableFilterButtonEvent = (filterName, filterValue, feature) => (_, getState) => {
  trackGa4Event('table_filter_select', {
    feature: feature || ANALYTICS_FEATURES.TABLE,
    filter_name: filterName,
    filter_value: String(filterValue).slice(0, 120),
  }, getState)
}

export const fireAnalyticsModalOpenEvent = () => (_, getState) => {
  trackGa4Event('analytics_modal_open', {
    feature: ANALYTICS_FEATURES.MODAL,
  }, getState)
}

export const firePageViewEvent = path => (_, getState) => {
  const pagePath = normalizePagePath(path || (typeof window !== 'undefined'
    ? window.location.pathname + window.location.search
    : ''))

  const now = Date.now()
  if (
    shouldSkipPageView(lastPageViewTracked.path, pagePath) ||
    (lastPageViewTracked.path === pagePath && now - lastPageViewTracked.at < PAGE_VIEW_DEDUPE_MS)
  ) {
    return
  }

  lastPageViewTracked = { path: pagePath, at: now }

  const [pathname, ...searchParts] = pagePath.split('?')
  const search = searchParts.length ? `?${searchParts.join('?')}` : ''
  const geography = parseDistrictDashboardPath(pathname)
  const property = parsePropertyLookupPath(pathname, search)

  trackGa4Event('page_view', {
    page_path: pagePath,
    page_location: typeof window !== 'undefined' ? window.location.href : undefined,
    ...(geography && {
      geography_type: geography.geographyType,
      geography_id: geography.geographyId,
      geography_label: getGeographyDisplayName(geography.geographyType),
    }),
    ...(property && {
      bbl: property.bbl,
      bin: property.bin,
      lookup_tab: property.lookup_tab,
    }),
  }, getState)

  if (geography) {
    trackGa4Event('district_dashboard_view', {
      feature: ANALYTICS_FEATURES.DISTRICT_DASHBOARD,
      geography_type: geography.geographyType,
      geography_id: geography.geographyId,
      geography_label: getGeographyDisplayName(geography.geographyType),
      source: 'page_view',
    }, getState)
  }
}
